import React from 'react';
import {Link, useParams} from 'react-router-dom';
import {ArrowRight, CheckCircle2, Clock, Wrench} from 'lucide-react';
import {useSeo} from '../seo/SeoProvider';
import NotFoundPage from './NotFoundPage';
const {productUrl} = require('../seo/registry');
const guides = require('../seo/guides.json');

const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function GuidePage() {
  const {slug} = useParams();
  const guide = guides[slug];
  const {products} = useSeo();

  if (!guide) return <NotFoundPage />;

  const available = (products || []).filter(product => product.isAvailable !== false);
  const matched = [];
  guide.materials.forEach(keyword => {
    if (matched.length >= 6) return;
    const found = available.find(product => normalize(product.name).includes(normalize(keyword)) && !matched.some(item => item.id === product.id));
    if (found) matched.push(found);
  });

  const origin = process.env.REACT_APP_SITE_URL;
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.h1,
    description: guide.description,
    datePublished: guide.date,
    dateModified: guide.date,
    image: `${origin}${guide.image}`,
    author: {'@type': 'Organization', name: '3DLoc'},
    publisher: {'@type': 'Organization', name: '3DLoc', logo: {'@type': 'ImageObject', url: `${origin}/logo-3dloc.jpg`}},
    mainEntityOfPage: `${origin}/guides/${guide.slug}`,
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {'@type': 'ListItem', position: 1, name: 'Accueil', item: `${origin}/`},
      {'@type': 'ListItem', position: 2, name: 'Guides pratiques', item: `${origin}/guides`},
      {'@type': 'ListItem', position: 3, name: guide.h1, item: `${origin}/guides/${guide.slug}`},
    ],
  };
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: guide.faq.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {'@type': 'Answer', text: item.answer},
    })),
  };

  return (
    <article data-testid="guide-page" className="bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(articleJsonLd)}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(breadcrumbJsonLd)}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(faqJsonLd)}} />

      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-14 sm:py-20">
          <nav aria-label="Fil d'Ariane" data-testid="guide-breadcrumb" className="text-sm text-slate-400 mb-6 flex flex-wrap gap-2">
            <Link to="/" className="hover:text-green-400 transition-colors">Accueil</Link>
            <span aria-hidden="true">/</span>
            <Link to="/guides" className="hover:text-green-400 transition-colors">Guides pratiques</Link>
          </nav>
          <p className="text-green-400 text-sm font-semibold uppercase tracking-wide mb-3">{guide.category}</p>
          <h1 data-testid="guide-title" className="text-4xl sm:text-5xl font-black leading-tight mb-5">{guide.h1}</h1>
          <p className="flex items-center gap-2 text-slate-400 text-sm"><Clock size={16} />Lecture {guide.readTime}</p>
        </div>
      </header>

      <img src={guide.image} alt={guide.h1} className="w-full h-56 sm:h-80 object-cover" />

      <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <div data-testid="guide-intro" className="space-y-4 text-base sm:text-lg text-slate-700 leading-relaxed">
          {guide.intro.split('\n\n').map((paragraph, index) => <p key={index}>{paragraph}</p>)}
        </div>

        {guide.sections.map((section, index) => (
          <section key={section.h2} data-testid={`guide-section-${index}`} className="mt-12">
            <h2 className="text-base md:text-lg font-bold text-slate-900 mb-4">{section.h2}</h2>
            <div className="space-y-4 text-sm sm:text-base text-slate-600 leading-relaxed">
              {section.paragraphs.map((paragraph, i) => <p key={i}>{paragraph}</p>)}
            </div>
            {section.checklist?.length > 0 && (
              <ul className="mt-5 space-y-2 bg-slate-50 border border-slate-100 rounded-xl p-5">
                {section.checklist.map(item => (
                  <li key={item} className="flex gap-3 text-sm sm:text-base text-slate-700">
                    <CheckCircle2 size={18} className="text-green-600 shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {matched.length > 0 && (
          <section data-testid="guide-materials" className="mt-14 bg-slate-50 border border-slate-100 rounded-xl p-6 sm:p-8">
            <h2 className="flex items-center gap-2 text-base md:text-lg font-bold text-slate-900 mb-5"><Wrench size={20} className="text-green-600" />Le matériel à louer pour ce chantier</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {matched.map(product => (
                <Link
                  key={product.id}
                  to={productUrl(product)}
                  data-testid={`guide-material-${product.id}`}
                  className="bg-white border border-slate-100 rounded-lg p-4 flex items-center gap-4 hover:border-green-300 hover:shadow-md transition-all"
                >
                  {product.image && <img src={product.image} alt={`${product.name} à louer chez 3DLoc`} loading="lazy" className="w-14 h-14 object-contain shrink-0" />}
                  <span className="min-w-0">
                    <span className="block font-semibold text-slate-800 text-sm line-clamp-2">{product.name}</span>
                    <span className="block text-sm text-green-700 mt-1">
                      {product.priceJour > 0 ? `${product.priceJour} € / jour` : product.priceWeekend > 0 ? `${product.priceWeekend} € / week-end` : 'Sur devis'}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold">
              {guide.families.map(family => (
                <Link key={family.path} to={family.path} className="text-green-700 hover:text-green-800 transition-colors">{family.label} →</Link>
              ))}
            </div>
          </section>
        )}

        <section data-testid="guide-faq" className="mt-14">
          <h2 className="text-base md:text-lg font-bold text-slate-900 mb-5">Questions fréquentes</h2>
          <div className="space-y-5">
            {guide.faq.map((item, index) => (
              <div key={item.question} data-testid={`guide-faq-${index}`} className="border-l-2 border-green-600 pl-4">
                <h3 className="font-semibold text-slate-800">{item.question}</h3>
                <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">{item.answer}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 bg-slate-900 text-white rounded-xl p-6 sm:p-8">
          <p className="text-slate-200 leading-relaxed">{guide.conclusion}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/categories" data-testid="guide-catalog-link" className="inline-flex items-center gap-2 px-5 py-3 bg-green-600 hover:bg-green-700 transition-colors font-semibold rounded-lg">
              Voir le catalogue <ArrowRight size={16} />
            </Link>
            <Link to="/contact" data-testid="guide-contact-link" className="px-5 py-3 border border-white/30 hover:bg-white/10 transition-colors font-semibold rounded-lg">
              Demander un devis
            </Link>
          </div>
        </section>
      </div>
    </article>
  );
}
